// this rule is suggesting to have the prop type definition inline in the component declaration
// disabling this rule as FloBuilder has lot of props
 
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react';
import { useTranslation } from 'react-i18next';
import { DEFAULT_CONFIG, contextId, flowBuilderConstants } from '../../constants/FlowBuilder';
import styles from './FlowBuilder.module.scss';
import {
  BaseFlowConfig,
  FlowBuilderProps,
  FlowBuilderState
} from './TypeDefs';
import {
  canvasDraw,
  canvasZoomIn,
  canvasZoomOut,
  getCanvasElements,
  handleCanvasMouseDown,
  handleCanvasMouseMove,
  handleCanvasMouseUp,
  handleCanvasZoom,
  slideElement,
  computeJourneyCardsAnchorTranslation,
  computeFlowItemFocusTranslation,
} from './helpers';

/**
 * Represents a Flow Builder element.
 */
export interface FlowBuilderElement {
  /**
   * Gets the current zoom level of the element.
   * @returns {number} The zoom level.
   */
  getZoom(): number;
  /**
   * Gets the current translation of the element.
   * @returns {{ x: number, y: number }} The translation.
   */
  getTranslation(): { x: number; y: number };
  /**
   * Sets the translation of the element.
   * @param {{ x: number, y: number }} position - The new translation position.
   */
  setTranslation(position: { x: number, y: number }): void;
  /**
   * Resets pan and zoom to defaults so the left-to-right journey-card layout is visible.
   */
  resetViewport(): void;
  /**
   * Resets zoom and aligns journey step cards to the fixed overview anchor.
   */
  resetJourneyCardsViewport(
    stepItems: { x: number; y: number }[],
    initiateStepCardVisible: boolean,
  ): void;
  /**
   * Resets zoom and pans so the given flow item is centred in the viewport.
   */
  focusFlowItemInViewport(
    x: number,
    y: number,
    viewportWidth: number,
    viewportHeight: number,
  ): void;
  /**
   * Gets the canvas element associated with the Flow Builder element.
   * @returns {HTMLCanvasElement | undefined | null} The canvas element.
   */
  getCanvas(): HTMLCanvasElement | undefined | null;
  /**
   * Slides an element to a specified position.
   * @param {string} id - The ID of the element to slide.
   * @param {{ x: number, y: number }} to - The target position to slide the element to.
   * @param {number} [duration] - The duration of the slide animation.
   * @returns {Promise<void>} A promise that resolves when the slide animation is complete.
   */
  slideElement(id: string, to: { x: number, y: number }, duration?: number): Promise<void>;
}

const FlowBuilderWithRef = ({
  className,
  items,
  stepItems,
  connectors,
  style = {},
  width,
  height,
  selectedItemNodeId,
  selectedConnector,
  config,
  startBtnSelected,
  startBtnVisible,
  initiateStepCardVisible,
  onConnectorDraw,
  onElementSelect,
  onConditionClick,
  onConnectorSelect,
  onDragComplete,
  onEgressClick,
  onStartBtnClick,
  onItemBtnClick,
  onConnectorBtnClick,
  onInitiateStepCardClick,
  onStepCardMenuClick,
  readOnly = false,
}: FlowBuilderProps, ref: React.Ref<FlowBuilderElement>) => {
  const { t: translate } = useTranslation();
  /**
   * Use `ref` instead of `state` to hold component data.
   * Canvas don't need react re-render to draw.
   * Using `state` might slowdown the component as
   * canvas can redraw many times in a sec while dragging.
   */
  const stateRef = useRef<FlowBuilderState>({
    canvasElements: [],
    mouseDownStart: 0,
    cursorStyle: 'default',
    zoomLevel: flowBuilderConstants.zoomLevelDefault,
    panningCanvas: false,
    translateX: flowBuilderConstants.translateXDefault,
    translateY: flowBuilderConstants.translateYDefault,
    flowConfig: DEFAULT_CONFIG,
    mousemove: { x: 0, y: 0 },
    clickingStartBtn: false,
    clickingInitiateStepCard: false
  });

  const [zoomLevel, setZoomLevel] = useState(stateRef.current.zoomLevel);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  // reset tooltip at starting
  if (tooltipRef.current) {
    tooltipRef.current.style.opacity = '0';
    tooltipRef.current.style.zIndex = '-1';
  }

  /**
   * @method drawElements
   * @description Draw elements on canvas and also draw the connector lines
   */
  const drawElements = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext(contextId);
    if (ctx) {
      canvasDraw({
        ctx,
        width: width / window.devicePixelRatio,
        height: height / window.devicePixelRatio,
        translateX: stateRef.current.translateX,
        translateY: stateRef.current.translateY,
        zoomLevel: stateRef.current.zoomLevel,
        canvasElements: stateRef.current.canvasElements,
        flowConfig: stateRef.current.flowConfig,
        startBtnSelected,
        startBtnVisible,
        initiateStepCardVisible,
        translate
      });
    }
  }, [width, height, startBtnSelected, startBtnVisible, initiateStepCardVisible]);

  /**
   * @method useImperativeHandle
   * @description react hook for fetching the ref using the callback function (1st parameter: ref, 2nd parameter: callback func)
   */
  useImperativeHandle(
    ref,
    () => ({
      getZoom() {
        return stateRef.current.zoomLevel;
      },
      getTranslation() {
        return {
          x: stateRef.current.translateX,
          y: stateRef.current.translateY
        };
      },
      setTranslation({ x, y }) {
        stateRef.current.translateX = x;
        stateRef.current.translateY = y;
        drawElements();
      },
      resetViewport() {
        stateRef.current.translateX = flowBuilderConstants.translateXDefault;
        stateRef.current.translateY = flowBuilderConstants.translateYDefault;
        stateRef.current.zoomLevel = flowBuilderConstants.zoomLevelDefault;
        setZoomLevel(flowBuilderConstants.zoomLevelDefault);
        drawElements();
      },
      resetJourneyCardsViewport(stepItems, initiateStepCardVisible) {
        stateRef.current.zoomLevel = flowBuilderConstants.zoomLevelDefault;
        setZoomLevel(flowBuilderConstants.zoomLevelDefault);
        const translation = computeJourneyCardsAnchorTranslation({
          stepItems,
          initiateStepCardVisible,
          zoomLevel: stateRef.current.zoomLevel,
        });
        stateRef.current.translateX = translation.x;
        stateRef.current.translateY = translation.y;
        drawElements();
      },
      focusFlowItemInViewport(x, y, viewportWidth, viewportHeight) {
        stateRef.current.zoomLevel = flowBuilderConstants.zoomLevelDefault;
        setZoomLevel(flowBuilderConstants.zoomLevelDefault);
        const translation = computeFlowItemFocusTranslation({
          x,
          y,
          viewportWidth,
          viewportHeight,
          zoomLevel: stateRef.current.zoomLevel,
        });
        stateRef.current.translateX = translation.x;
        stateRef.current.translateY = translation.y;
        drawElements();
      },
      getCanvas() {
        return canvasRef.current;
      },
      slideElement(
        id: string,
        to: { x: number, y: number },
        duration = 700,
      ): Promise<void> {
        return slideElement({
          canvasElements: stateRef.current.canvasElements,
          drawElements,
          duration,
          id,
          to
        });
      }
    }),
    [drawElements]
  );

  // change the canvas element properties based on new prop values
  useEffect(() => {
    const { canvasElements } = stateRef.current;
    const newConfig: BaseFlowConfig = { ...DEFAULT_CONFIG, ...config };
    newConfig.startBtnText = translate('journey_builder_component.txt_start');
    stateRef.current.canvasElements = getCanvasElements({
      canvasElements,
      connectors,
      items,
      stepItems,
      newConfig,
      selectedConnector,
      selectedItemNodeId,
    }).newElements;
    stateRef.current.flowConfig = newConfig;
    // redraw the canvas after changing elements
    drawElements();
  }, [
    stepItems,
    items,
    connectors,
    selectedConnector,
    selectedItemNodeId,
    config,
    drawElements,
    width,
    height,
    translate
  ]);

  /**
   * @method mouseDownHandler
   * @description canvas mouse down handler
   * @param {React.MouseEvent} ev
   */
  const mouseDownHandler = (ev: React.MouseEvent) => {
    const canvas = canvasRef.current;
    if (!canvas || ev.button !== 0) {
      return;
    }
    const { top, left } = canvas.getBoundingClientRect();
    const mouseX = (ev.clientX - left - stateRef.current.translateX) / stateRef.current.zoomLevel;
    const mouseY = (ev.clientY - top - stateRef.current.translateY) / stateRef.current.zoomLevel;

    handleCanvasMouseDown({
      canvas,
      flowBuilderState: stateRef.current,
      mouseX,
      mouseY,
      startBtnVisible,
      initiateStepCardVisible
    });
  };

  /**
   * @method mouseMoveHandler
   * @description canvas mouse move handler
   * @param {React.MouseEvent} ev
   */
  const mouseMoveHandler = (ev: React.MouseEvent) => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    const tooltipElem = tooltipRef.current;
    if (!canvas || !ctx || !tooltipElem || ev.button !== 0) {
      return;
    }
    const { top, left } = canvas.getBoundingClientRect();
    const mouseX = (ev.clientX - left - stateRef.current.translateX) / stateRef.current.zoomLevel;
    const mouseY = (ev.clientY - top - stateRef.current.translateY) / stateRef.current.zoomLevel;
    handleCanvasMouseMove({
      canvas,
      clientX: ev.clientX,
      clientY: ev.clientY,
      ctx,
      drawElements,
      flowBuilderState: stateRef.current,
      mouseX,
      mouseY,
      movementX: ev.movementX,
      movementY: ev.movementY,
      startBtnVisible,
      tooltipElem,
      initiateStepCardVisible,
      readOnly: !!readOnly
    });
  };

  /**
   * @method mouseUpHandler
   * @description canvas mouse up handler
   * @param {React.MouseEvent} ev
   */
  const mouseUpHandler = (ev: React.MouseEvent) => {
    const canvas = canvasRef.current;
    if (!canvas || ev.button !== 0) {
      return;
    }
    const { top, left } = canvas.getBoundingClientRect();
    const mouseX = (ev.clientX - left - stateRef.current.translateX) / stateRef.current.zoomLevel;
    const mouseY = (ev.clientY - top - stateRef.current.translateY) / stateRef.current.zoomLevel;
    handleCanvasMouseUp({
      canvas,
      drawElements,
      flowBuilderState: stateRef.current,
      mouseX,
      mouseY,
      clientX: ev.clientX,
      clientY: ev.clientY,
      onConditionClick: readOnly ? undefined : onConditionClick,
      onConnectorBtnClick,
      onConnectorDraw: readOnly ? undefined : onConnectorDraw,
      onConnectorSelect,
      onDragComplete,
      onEgressClick: readOnly ? undefined : onEgressClick,
      onElementSelect,
      onItemBtnClick,
      onStartBtnClick,
      onInitiateStepCardClick,
      onStepCardMenuClick,
    });
  };

  /**
   * @method mouseLeaveHandler
   * @description canvas mouse leave handler
   */
  const mouseLeaveHandler = () => {
    stateRef.current.draggingElement = undefined;
    stateRef.current.mouseDownStart = 0;
    stateRef.current.panningCanvas = false;
    stateRef.current.clickingStartBtn = false;
    stateRef.current.clickingInitiateStepCard = false;
    stateRef.current.mousemove = { x: 0, y: 0 };
    stateRef.current.drawingConnectorElement = undefined;
    drawElements();
    stateRef.current.cursorStyle = flowBuilderConstants.defaultCursor;
    if (canvasRef.current) {
      canvasRef.current.style.cursor = stateRef.current.cursorStyle;
    }
  };

  /**
   * @method wheelHandler
   * @description canvas mouse wheel handler
   * @param {React.WheelEvent} ev
   */
  const wheelHandler = (ev: React.WheelEvent) => {
    const canvas = canvasRef.current;
    const { top, left } = canvas?.getBoundingClientRect() ?? { top: 0, left: 0 };
    const { zoomLevel, translateX, translateY } = handleCanvasZoom({
      zoomLevel: stateRef.current.zoomLevel,
      translateX: stateRef.current.translateX,
      translateY: stateRef.current.translateY,
      top,
      left,
      clientX: ev.clientX,
      clientY: ev.clientY,
      deltaY: ev.deltaY
    });
    stateRef.current.zoomLevel = zoomLevel;
    stateRef.current.translateX = translateX;
    stateRef.current.translateY = translateY;
    setZoomLevel(stateRef.current.zoomLevel);
    drawElements();
  };

  /**
   * @method zoomInHandler
   * @description canvas Zoom In handler
   */
  const zoomInHandler = () => {
    const { zoomLevel, translateX, translateY } = canvasZoomIn(
      stateRef.current.zoomLevel,
      stateRef.current.translateX,
      stateRef.current.translateY,
      width,
      height
    );
    stateRef.current.zoomLevel = zoomLevel;
    stateRef.current.translateX = translateX;
    stateRef.current.translateY = translateY;
    setZoomLevel(stateRef.current.zoomLevel);
    drawElements();
  };

  /**
   * @method zoomInHandler
   * @description canvas Zoom Out handler
   */
  const zoomOutHandler = () => {
    const { zoomLevel, translateX, translateY } = canvasZoomOut(
      stateRef.current.zoomLevel,
      stateRef.current.translateX,
      stateRef.current.translateY,
      width,
      height
    );
    stateRef.current.zoomLevel = zoomLevel;
    stateRef.current.translateX = translateX;
    stateRef.current.translateY = translateY;
    setZoomLevel(stateRef.current.zoomLevel);
    drawElements();
  };

  const canvasStyle: React.CSSProperties = { ...style };
  canvasStyle.cursor = stateRef.current.cursorStyle;
  return (
    <>
      <div className={styles.tooltip} ref={tooltipRef} data-testid="flowBuilderTooltip" />
      <canvas
        data-testid="flowBuilderCanvas"
        className={className}
        ref={canvasRef}
        onMouseDown={mouseDownHandler}
        onMouseMove={mouseMoveHandler}
        onMouseUp={mouseUpHandler}
        onMouseLeave={mouseLeaveHandler}
        style={canvasStyle}
        width={width}
        height={height}
        onWheel={wheelHandler}
        onDragOver={(ev) => ev.preventDefault()}
      />
      <div className={styles.zoom}>
        <button type="button" data-testid="flowBuilderZoomOut" onClick={zoomOutHandler} aria-label="zoom-out">
          <svg viewBox="0 0 24 24">
            <path
              fill="currentColor"
              stroke="currentColor"
              d="M19,13H5V11H19V13Z"
            />
          </svg>
        </button>
        <span>{(zoomLevel * 100).toFixed(0)}%</span>
        <button type="button" data-testid="flowBuilderZoomIn" onClick={zoomInHandler} aria-label="zoom-in">
          <svg viewBox="0 0 24 24">
            <path
              fill="currentColor"
              stroke="currentColor"
              d="M19,13H13V19H11V13H5V11H11V5H13V11H19V13Z"
            />
          </svg>
        </button>
      </div>
    </>
  );
};

/**
 * A component to build flow using canvas
 */
const FlowBuilder = forwardRef<FlowBuilderElement, FlowBuilderProps>(
  (
    props,
    ref
  ) => FlowBuilderWithRef(props, ref)
);
export default FlowBuilder;
